import React from 'react';
import { Heart, Globe, Lightbulb, Users } from 'lucide-react';
import { Card } from './ui/card';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Transformation',
      description: 'Living transformation as a way of being — as a husband, father, leader, and creator.'
    },
    {
      icon: Globe,
      title: 'Global Impact',
      description: 'Creating platforms and movements that empower humanity to evolve across 120+ countries.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'Building AI systems designed for elevating human consciousness, not just efficiency.'
    },
    {
      icon: Users,
      title: 'Leadership',
      description: '30+ years supporting thousands in discovering their capacity to lead powerful lives.'
    }
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-black to-[#0B1D2A] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            About <span className="text-[#D4AF37]">Charles Crawshaw</span>
          </h2>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto rounded-full" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div className="relative">
            <div className="absolute inset-0 bg-[#D4AF37]/20 rounded-2xl blur-xl transform rotate-3" />
            <img
              src="/smiling-charles.png"
              alt="Charles Crawshaw"
              className="relative rounded-2xl shadow-2xl w-full h-auto object-cover border-2 border-[#D4AF37]/30"
            />
          </div>

          {/* Content */}
          <div className="space-y-6">
            <p
              className="text-lg text-white/80 leading-relaxed"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              I live from <span className="text-[#D4AF37] font-semibold">transformation as a way of being</span> — as a husband, father, leader, and creator. My life's work is grounded in the belief that when individuals transform, the world transforms.
            </p>
            <p
              className="text-lg text-white/80 leading-relaxed"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              My journey spans over three decades of deep work with <span className="text-[#D4AF37] font-semibold">Landmark Worldwide</span>, where I developed a foundation in leadership, communication, and human potential. Through coaching, facilitation, and global initiatives, I have supported thousands of individuals in discovering their capacity to lead powerful, self-expressed lives.
            </p>
            <p
              className="text-lg text-white/80 leading-relaxed"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Today, I operate at the intersection of <span className="text-[#D4AF37] font-semibold">artificial intelligence, education, and global impact</span>. As Co-Creator of Hermetix.ai, I am building AI systems designed not just for efficiency, but for elevating human consciousness — integrating ethics, emotional intelligence, and creativity into the future of technology.
            </p>
            <p
              className="text-lg text-white/80 leading-relaxed"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Alongside this, I serve as Executive Marketing Manager for <span className="text-[#D4AF37] font-semibold">The Great Discovery</span>, a global e-learning platform delivering transformational education in over 120 languages.
            </p>
            <div className="pt-4 border-t border-[#D4AF37]/30">
              <p
                className="text-2xl font-bold text-[#D4AF37] text-center"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                My Commitment: To cause World Peace — not as an idea, but as a lived reality.
              </p>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <Card
              key={index}
              className="bg-[#0B1D2A]/50 border-[#D4AF37]/30 p-6 hover:border-[#D4AF37] transition-all duration-300 hover:transform hover:scale-105 backdrop-blur-sm"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3
                  className="text-xl font-bold text-white"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {value.title}
                </h3>
                <p
                  className="text-sm text-white/70"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  {value.description}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
